/**
 * A campaign run's report, as the server freezes it (internal/campaign.Report).
 * Written out here rather than generated: the API carries it as an open object
 * so the stored format can grow without a schema change for every field.
 */
export interface RunReport {
  version: number;
  assessment?: { version: number; verdict: string; results: { name: string; scope: string; metric: string; threshold: number; actual?: number; verdict: string; reason: string }[] };
  inventory?: {
    version: number; captured_at: string; lease_acknowledgement: string; acceptance?: { version: number; objectives: import("./Objectives").Objective[] };
    series: { agent_id: string; test_id: string; test: string; kind: string; target: string; configured_interval_ms: number; requested_interval_ms: number; summarized: boolean }[];
  };
  frozen_at?: string;
  late_arrival_policy?: string;
  campaign: string;
  case: string;
  started_at: string;
  stopped_at: string;
  duration_seconds: number;
  step_seconds: number;
  agents: { id: string; name: string }[];
  series: ReportSeries[];
  outages: ReportOutage[];
  events: ReportEvent[];
  sessions?: ReportSession[];
  /** The application mix the playbook ran, if it had one. */
  traffic?: {
    profile: string;
    client: string;
    responder: string;
    session_id?: string;
    state: string;
    path?: string;
    error?: string;
    result?: import("../traffic").TrafficResultData;
  };
  truncated?: string;
}

export interface ReportSeries {
  test_id: string;
  test: string;
  kind: string;
  target: string;
  agent_id: string;
  agent: string;
  evidence?: { first_sample_at: string; last_sample_at: string; largest_gap_seconds: number };
  summary: {
    samples: number;
    lost: number;
    loss_ratio: number;
    avg_ms?: number;
    min_ms?: number;
    max_ms?: number;
    p95_ms?: number;
    outages: number;
    longest_outage_seconds?: number;
    first_loss_after_seconds?: number;
    down_seconds: number;
  };
  buckets: { t: string; samples: number; lost: number; avg_ms?: number; max_ms?: number }[];
}

export interface ReportOutage {
  test: string;
  agent: string;
  start: string;
  end: string;
  recovered: boolean;
  seconds: number;
  lost: number;
  after_start_seconds: number;
}

export interface ReportEvent {
  t: string;
  agent?: string;
  category: string;
  type: string;
  severity: string;
  summary: string;
}

export interface ReportSession {
  t: string;
  kind: string;
  client: string;
  responder: string;
  state: string;
  result?: string;
}

/** A named moment in a run (POST /campaign-runs/{id}/markers). */
export interface Marker {
  id: string;
  at: string;
  label: string;
  created_by: string;
}

/** What followed a marker: the first outage to begin at or after it, and when that one ended. */
export interface MarkerTiming {
  marker: Marker;
  intoRun: number;
  lossAfter?: number;
  restoredAfter?: number;
  /** Where the first loss was. */
  where?: string;
}

/**
 * Times each marker against the outages: the run's own figures are measured
 * from Start, which is when someone pressed a button; a marker is when the
 * thing being tested actually happened. An outage that began up to one bucket
 * before the marker still counts — the marker is typed a moment after the
 * cable is pulled.
 */
export function markerTimings(r: RunReport, markers: Marker[]): MarkerTiming[] {
  const t0 = Date.parse(r.started_at);
  const slack = r.step_seconds * 1000;
  return [...markers].sort((a, b) => a.at.localeCompare(b.at)).map((m) => {
    const at = Date.parse(m.at);
    const next = r.outages
      .filter((o) => Date.parse(o.start) >= at - slack)
      .sort((a, b) => a.start.localeCompare(b.start))[0];
    const out: MarkerTiming = { marker: m, intoRun: Math.max(0, (at - t0) / 1000) };
    if (next) {
      out.lossAfter = Math.max(0, (Date.parse(next.start) - at) / 1000);
      out.where = `${next.test} on ${next.agent}`;
      if (next.recovered) out.restoredAfter = Math.max(0, (Date.parse(next.end) - at) / 1000);
    }
    return out;
  });
}

/** One test on one agent, in this run and in the baseline. */
export interface Comparison {
  test: string;
  agent: string;
  run?: ReportSeries["summary"];
  base?: ReportSeries["summary"];
}

/** Pairs this run's series with the baseline's by test and agent; a series in only one of them is kept, with the other side empty. */
export function compareRuns(r: RunReport, base: RunReport): Comparison[] {
  const key = (s: ReportSeries) => `${s.test_id}/${s.agent_id}`;
  const out = new Map<string, Comparison>();
  for (const s of base.series) out.set(key(s), { test: s.test, agent: s.agent, base: s.summary });
  for (const s of r.series) out.set(key(s), { ...(out.get(key(s)) ?? { test: s.test, agent: s.agent }), run: s.summary });
  return [...out.values()].sort((a, b) => a.test.localeCompare(b.test) || a.agent.localeCompare(b.agent));
}

/** "+3.2 ms", "−0.4 pp": how far a figure moved from the baseline. */
export function delta(run: number | undefined, base: number | undefined, unit: "ms" | "pp" | ""): string {
  if (run == null || base == null) return "—";
  const d = unit === "pp" ? (run - base) * 100 : run - base;
  if (Math.abs(d) < (unit === "" ? 0.5 : 0.005)) return "no change";
  const sign = d > 0 ? "+" : "−";
  const v = Math.abs(d);
  return `${sign}${unit === "" ? Math.round(v) : v.toFixed(v < 10 ? 2 : 1)}${unit === "ms" ? " ms" : unit === "pp" ? " pp" : ""}`;
}

/** "1 h 02 min", "3 min 05 s", "4.2 s": a duration as a person reads one. */
export function duration(seconds: number): string {
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m} min ${String(s).padStart(2, "0")} s`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

export function ms(v: number | undefined): string {
  return v == null ? "—" : `${v.toFixed(v < 10 ? 2 : 1)} ms`;
}

export function pct(r: number): string {
  return `${(r * 100).toFixed(r > 0 && r < 0.001 ? 3 : 2)}%`;
}

/** Time of day with seconds: the resolution failovers are read at. */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/**
 * One series as a standalone SVG: round-trip time as a line, lost samples as
 * red bars, outages shaded. Standalone — explicit colours, no stylesheet — so
 * the same drawing goes on the page and, rasterised, into the Word document.
 */
export function seriesChartSVG(s: ReportSeries, r: RunReport, opts: { width?: number; height?: number; markers?: Marker[] } = {}): string {
  const W = opts.width ?? 900;
  const H = opts.height ?? 260;
  const pad = { l: 56, r: 16, t: 16, b: 34 };
  const t0 = Date.parse(r.started_at);
  const t1 = Math.max(t0 + 1000, Date.parse(r.stopped_at));
  const vals = s.buckets.map((b) => b.max_ms ?? b.avg_ms ?? 0);
  const top = Math.max(1, ...vals) * 1.15;
  const x = (t: number) => pad.l + ((t - t0) / (t1 - t0)) * (W - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  const step = r.step_seconds * 1000;
  const out: string[] = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="Helvetica, Arial, sans-serif">`);
  out.push(`<rect width="${W}" height="${H}" fill="#ffffff"/>`);
  for (const o of r.outages.filter((o) => o.test === s.test && o.agent === s.agent)) {
    const a = x(Date.parse(o.start));
    const b = x(Date.parse(o.end));
    out.push(`<rect x="${a.toFixed(1)}" y="${pad.t}" width="${Math.max(1, b - a).toFixed(1)}" height="${H - pad.t - pad.b}" fill="#fee2e2"/>`);
  }
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    const v = top * f;
    out.push(`<line x1="${pad.l}" x2="${W - pad.r}" y1="${y(v).toFixed(1)}" y2="${y(v).toFixed(1)}" stroke="#e5e7eb"/>`);
    out.push(`<text x="${pad.l - 8}" y="${(y(v) + 4).toFixed(1)}" font-size="11" text-anchor="end" fill="#6b7280">${v.toFixed(v < 10 ? 1 : 0)}</text>`);
  }
  out.push(`<text x="14" y="${(pad.t + (H - pad.t - pad.b) / 2).toFixed(1)}" font-size="11" fill="#6b7280" transform="rotate(-90 14 ${(pad.t + (H - pad.t - pad.b) / 2).toFixed(1)})" text-anchor="middle">RTT (ms)</text>`);
  for (const b of s.buckets) {
    if (b.lost === 0) continue;
    const bx = x(Date.parse(b.t));
    const bw = Math.max(1, x(Date.parse(b.t) + step) - bx);
    const frac = b.lost / Math.max(1, b.samples);
    const bh = frac * (H - pad.t - pad.b);
    out.push(`<rect x="${bx.toFixed(1)}" y="${(H - pad.b - bh).toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" fill="#dc2626" fill-opacity="0.75"/>`);
  }
  // The line breaks where a bucket has no delivered sample, rather than drawing
  // a slope across an outage that never happened.
  let path = "";
  let pen = false;
  for (const b of s.buckets) {
    if (b.avg_ms == null) { pen = false; continue; }
    path += `${pen ? "L" : "M"}${x(Date.parse(b.t)).toFixed(1)},${y(b.avg_ms).toFixed(1)} `;
    pen = true;
  }
  if (path) out.push(`<path d="${path.trim()}" fill="none" stroke="#2563eb" stroke-width="1.6"/>`);
  // Markers: a dashed line at each, labelled along the top.
  for (const m of opts.markers ?? []) {
    const mt = Date.parse(m.at);
    if (mt < t0 || mt > t1) continue;
    const mx = x(mt).toFixed(1);
    out.push(`<line x1="${mx}" x2="${mx}" y1="${pad.t}" y2="${H - pad.b}" stroke="#7c3aed" stroke-dasharray="4 3" stroke-width="1.2"/>`);
    out.push(`<text x="${(x(mt) + 4).toFixed(1)}" y="${pad.t + 11}" font-size="11" fill="#7c3aed">${escapeXML(m.label.length > 40 ? m.label.slice(0, 39) + "…" : m.label)}</text>`);
  }
  out.push(`<line x1="${pad.l}" x2="${W - pad.r}" y1="${H - pad.b}" y2="${H - pad.b}" stroke="#9ca3af"/>`);
  const span = t1 - t0;
  const ticks = [5e3, 10e3, 15e3, 30e3, 60e3, 120e3, 300e3, 600e3, 900e3, 1800e3, 3600e3].find((t) => span / t <= 8) ?? 7200e3;
  for (let t = Math.ceil(t0 / ticks) * ticks; t <= t1; t += ticks) {
    out.push(`<line x1="${x(t).toFixed(1)}" x2="${x(t).toFixed(1)}" y1="${H - pad.b}" y2="${H - pad.b + 4}" stroke="#9ca3af"/>`);
    out.push(`<text x="${x(t).toFixed(1)}" y="${H - pad.b + 17}" font-size="11" text-anchor="middle" fill="#374151">${escapeXML(clock(new Date(t).toISOString()))}</text>`);
  }
  out.push(`</svg>`);
  return out.join("");
}

export function escapeXML(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c]!);
}

/** Shared with the exported document: absence of recorded loss is not uptime. */
export function evidenceNotes(report: RunReport): string[] {
  const notes = ["Loss and outage totals describe recorded probes only. Missing measurements are unknown, not successful probes. Continuous coverage is not verified; availability cannot be established from these totals."];
  if (report.inventory) {
    notes.push(`Expected inventory captured at start. Intervals are configured/requested intent, not confirmed cadence. ${report.inventory.lease_acknowledgement}`);
    const missingSeries = report.inventory.series.filter(item => item.summarized && !report.series.some(s => s.agent_id === item.agent_id && s.test_id === item.test_id && s.summary.samples > 0));
    if (missingSeries.length) notes.push(`Expected series without recorded samples: ${missingSeries.map(item => `${report.agents.find(a => a.id === item.agent_id)?.name || item.agent_id} / ${item.test}`).join(", ")}.`);
    if (!report.inventory.series.length) notes.push("No enabled tests were assigned to the shards at run start.");
  } else notes.push("Expected test inventory and cadence were not recorded for this historical run.");
  notes.push(report.frozen_at && report.late_arrival_policy ? `Frozen ${new Date(report.frozen_at).toLocaleString()}. ${report.late_arrival_policy}` : "Freeze timing and late-arrival policy were not recorded in this historical report.");
  const missing = report.agents.filter((a) => !report.series.some((s) => s.agent_id === a.id));
  if (missing.length) notes.push(`No measurements recorded for: ${missing.map((a) => a.name || a.id).join(", ")}.`);
  if (report.series.some((s) => !s.evidence)) notes.push("Sampling timing was not recorded in this historical report.");
  notes.push("Outage durations are bounded by probe observations. Address or interface events provide context and do not establish packet loss by themselves.");
  return notes;
}
export function noObservedOutages(report: RunReport): string {
  return report.series.some((s) => s.summary.samples > 0)
    ? "No loss observed in the recorded samples. Gaps between observations may conceal an interruption."
    : "No measurements recorded; outage status is unknown.";
}
